import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  FaSchool,
  FaUsersCog,
  FaMoneyBill,
  FaUserShield,
  FaPlus,
  FaEye,
} from "react-icons/fa";
import api from "../../api";

const SuperAdminDashboard = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    schools: 0,
    admins: 0,
    subscriptions: 0,
    revenue: 0,
  });

  useEffect(() => {
    let aborted = false;
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      let aborted = false;

      try {
        const res = await api.get("/super_admin/dashboard/get_data");

        if (res.data?.status && !aborted) {
          setData(res.data.data);
        }
      } catch (err) {
        console.error("SuperAdmin Dashboard fetch error:", err);
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      aborted = true;
      controller.abort();
    };
  }, []);

  // 👉 Stats cards
  const statsCards = [
    {
      id: "schools",
      label: "Total Schools",
      icon: <FaSchool />,
      color: "#1e88e5",
      value: data.schools,
      routeOnCard: "/super_admin/schools",
      routeOnButton: "/super_admin/schools",
      buttonLabel: "Add School",
      buttonIcon: <FaPlus />,
    },
    {
      id: "admins",
      label: "Admins & Staff",
      icon: <FaUsersCog />,
      color: "#43a047",
      value: data.admins,
      routeOnCard: "/super_admin/schooladmins",
      routeOnButton: "/super_admin/schooladmins",
      buttonLabel: "Manage",
      buttonIcon: <FaEye />,
    },
    {
      id: "subscriptions",
      label: "Active Subscriptions",
      icon: <FaUserShield />,
      color: "#fb8c00",
      value: data.subscriptions,
      routeOnCard: "/super_admin/subscriptions",
      routeOnButton: "/super_admin/subscriptions",
      buttonLabel: "Renewals",
      buttonIcon: <FaEye />,
    },
    {
      id: "revenue",
      label: "Total Revenue",
      icon: <FaMoneyBill />,
      color: "#0f766e",
      value: `₹ ${new Intl.NumberFormat("en-IN").format(data.revenue)}`,
      routeOnCard: "/super_admin/revenue",
      routeOnButton: "/super_admin/revenue",
      buttonLabel: "View All",
      buttonIcon: <FaEye />,
    },
  ];

  // 👉 Management menu cards
  const menuCards = [
    {
      id: "schools",
      label: "Schools",
      icon: <FaSchool />,
      color: "#1e88e5",
      routeOnCard: "/super_admin/schools",
    },
    {
      id: "subscriptions",
      label: "Subscriptions",
      icon: <FaUserShield />,
      color: "#fb8c00",
      routeOnCard: "/super_admin/subscriptions",
    },
    {
      id: "admins",
      label: "Admins & Staff",
      icon: <FaUsersCog />,
      color: "#43a047",
      routeOnCard: "/super_admin/schooladmins",
    },
    {
      id: "accounts-expense",
      label: "Accounts & Expense",
      icon: <FaMoneyBill />,
      color: "#0f766e",
      routeOnCard: "/super_admin/accounts-expense",
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", background: "transparent" }}>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 260,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Stats Cards */}
          <Box
            sx={{
              p: { xs: 2, md: 2.5 },
              mb: 3.5,
              borderRadius: 2,
              background: "linear-gradient(135deg, rgba(255,255,255,0.72), rgba(231,248,246,0.58))",
              border: "1px solid rgba(8,145,178,0.14)",
              boxShadow: "0 14px 34px rgba(16,42,67,0.06)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
              <Box>
                <Typography sx={{ fontWeight: 900, color: "#102a43", fontSize: { xs: 22, md: 26 }, letterSpacing: 0 }}>
                  Platform Overview
                </Typography>
                <Typography sx={{ mt: 0.5, color: "#607086", fontSize: 14, fontWeight: 600 }}>
                  A clear view of your platform activity
                </Typography>
              </Box>
              <Box sx={{ display: { xs: "none", sm: "block" }, px: 1.25, py: 0.65, borderRadius: 999, color: "#0f766e", background: "rgba(15,118,110,0.1)", fontSize: 12, fontWeight: 800 }}>
                LIVE SUMMARY
              </Box>
            </Box>
            <Grid container spacing={2.5}>
            {statsCards.map((card) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={card.id}>
                <Paper
                  elevation={3}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: { xs: 1.75, md: 2 },
                    borderRadius: 2,
                    minHeight: 124,
                    background:
                      "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(248,251,255,0.9))",
                    border: "1px solid rgba(78,102,135,0.16)",
                    borderTop: `3px solid ${card.color}`,
                    boxShadow: "0 12px 28px rgba(23,32,51,0.09)",
                    transition: "transform .2s ease, box-shadow .2s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 18px 42px rgba(23,32,51,0.14)",
                    },
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(card.routeOnCard)}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 1.5,
                      display: "grid",
                      placeItems: "center",
                      background: `${card.color}22`,
                    }}
                  >
                    <Box sx={{ color: card.color, fontSize: 28 }}>{card.icon}</Box>
                  </Box>
                  <Box sx={{ flex: 1, px: 1.5, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#607086" }}>
                      {card.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: 22, md: 25 }, color: "#172033", lineHeight: 1.15 }}>
                      {card.value}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(card.routeOnButton);
                    }}
                    startIcon={card.buttonIcon}
                    sx={{
                      background: `linear-gradient(90deg, ${card.color}, ${card.color}cc)`,
                      color: "white",
                      fontWeight: 700,
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.8,
                    }}
                  >
                    {card.buttonLabel}
                  </Button>
                </Paper>
              </Grid>
            ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 3.5, borderColor: "rgba(78,102,135,0.18)" }} />

          {/* Menu Cards */}
          <Box
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 2,
              background: "linear-gradient(135deg, rgba(255,255,255,0.72), rgba(231,248,246,0.58))",
              border: "1px solid rgba(8,145,178,0.14)",
              boxShadow: "0 14px 34px rgba(16,42,67,0.06)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
              <Box>
                <Typography sx={{ fontWeight: 900, color: "#102a43", fontSize: 22, letterSpacing: 0 }}>
                  Management
                </Typography>
                <Typography sx={{ mt: 0.5, color: "#607086", fontSize: 14, fontWeight: 600 }}>
                  Quick access to platform operations
                </Typography>
              </Box>
              <Box sx={{ display: { xs: "none", sm: "block" }, px: 1.25, py: 0.65, borderRadius: 999, color: "#2563eb", background: "rgba(37,99,235,0.1)", fontSize: 12, fontWeight: 800 }}>
                CONTROL CENTER
              </Box>
            </Box>
            <Grid container spacing={2.5}>
            {menuCards.map((m) => (
              <Grid item xs={12} sm={6} md={4} key={m.id}>
                <Paper
                  elevation={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    minHeight: 104,
                    background:
                      "linear-gradient(145deg, rgba(255,255,255,0.96), rgba(248,251,255,0.9))",
                    border: "1px solid rgba(78,102,135,0.16)",
                    borderTop: `3px solid ${m.color}`,
                    boxShadow: "0 12px 28px rgba(23,32,51,0.09)",
                    textAlign: "left",
                    transition: "all .2s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 18px 42px rgba(23,32,51,0.14)",
                    },
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(m.routeOnCard)}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      background: `${m.color}22`,
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ color: m.color, fontSize: 28 }}>{m.icon}</Box>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#172033" }}>
                      {m.label}
                    </Typography>
                    <Typography sx={{ color: "#607086", fontSize: 12, mt: 0.25 }}>
                      Manage platform records
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<FaEye />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(m.routeOnCard);
                    }}
                    sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800, flexShrink: 0 }}
                  >
                    Open
                  </Button>
                </Paper>
              </Grid>
            ))}
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SuperAdminDashboard;
